import clsx from 'clsx';

import Typography from '~/shared/components/Typography';

import { formatBytes, truncate } from '../../lib/formatBytes';

import type { FocusEvent, MouseEvent } from 'react';

interface StatusRowProps {
  label: string;
  size: number;
  count: number;
  pct: number;
  barColor: string;
  selected?: boolean;
  onClick?: () => void;
  onMouseEnter?: (e: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: () => void;
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
  onBlur?: () => void;
}

export default function StatusRow({
  label,
  size,
  count,
  pct,
  barColor,
  selected = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
}: StatusRowProps) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? selected : undefined}
      aria-label={onClick ? `${label}, ${count.toLocaleString()} volumes` : undefined}
      className={clsx(
        'group grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 items-center px-3 py-2.5  bg-transparent text-left min-h-9',
        ' hover:bg-primary-20 transition-colors duration-[120ms] rounded-lg',
        selected && 'bg-primary-20',
        onClick &&
          'cursor-pointer focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-1'
      )}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className="min-w-0 flex items-center justify-between">
        <Typography
          variant="caption"
          fontWeight={500}
          color="text-primary-900"
          className={clsx(
            'truncate group-hover:text-primary-500 min-w-28',
            selected && 'text-primary-500'
          )}
        >
          {truncate(label, 15)}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={500}
          color="text-gray-700"
          className="tabular-nums min-w-10 text-right"
        >
          {formatBytes(size)}
        </Typography>
        <Typography
          variant="caption"
          color="text-gray-700"
          className="tabular-nums min-w-10 text-right"
        >
          ({count.toLocaleString()})
        </Typography>
      </div>
      <div className="flex items-center">
        <div className="relative w-[114px] h-3.5 rounded shrink-0 overflow-hidden border border-gray-300">
          <div
            className="absolute inset-y-0 left-0 rounded-l"
            style={{
              width: `${Math.min(100, pct)}%`,
              background: barColor,
            }}
          />
        </div>
        <Typography
          variant="caption"
          color="text-gray-700"
          className="tabular-nums min-w-10 text-right"
        >
          {Math.round(pct)}%
        </Typography>
      </div>
    </div>
  );
}
